import { useRef, useState, useEffect, useCallback } from 'react';
import { Camera, Trash2, Save, User, Mail, Bell, ShieldCheck, BadgeCheck, AlertTriangle, CreditCard, CheckCircle, ExternalLink, Loader, Moon, Sun, Palette, Ban } from 'lucide-react';
import { useCurrentUser, useUploadAvatar, useDeleteAvatar, useUpdateProfile, useEmailPreferences, useUpdateEmailPreferences, useSendVerificationEmail, useRequestDeleteAccount, useConnectStatus, useOnboardConnect, useBlockedUsers, useUnblockUser } from '@/hooks/useApi';
import { useAuthStore, useThemeStore } from '@/store';
import UserAvatar from '@/components/UserAvatar';
import AvatarCropModal from '@/components/AvatarCropModal';
import type { EmailPreferences } from '@/lib/api';
import { apiClient } from '@/lib/api-client';

const EMAIL_PREF_OPTIONS: { key: keyof EmailPreferences; label: string; description: string }[] = [
  { key: 'auction_won',     label: 'Auction won',              description: 'You placed the winning bid on an auction'         },
  { key: 'auction_ended',   label: 'Auction ended',            description: 'One of your auction listings has closed'          },
  { key: 'auction_expired', label: 'Auction expired',          description: 'Your auction ended with no bids'                  },
  { key: 'wishlist_match',  label: 'Wishlist match',           description: 'A listing appears that matches your wishlist'     },
  { key: 'wishlist_seller', label: 'Wishlist seller alert',    description: "A buyer's wishlist matches a card you're selling" },
  { key: 'set_watch_match', label: 'Set watch alert',          description: 'A new card from a set you\'re watching is listed' },
  { key: 'new_message',     label: 'New message',              description: 'Someone sends you a message'                      },
  { key: 'trade_offer',    label: 'Trade offer updates',      description: 'You receive, accept, decline, or counter a trade offer' },
  { key: 'order_update',   label: 'Order updates',            description: 'Your order is confirmed, shipped, or delivered'   },
];

function BlockedUsersSection() {
  const { data: blockedUsers = [], isLoading } = useBlockedUsers();
  const { mutate: unblock, isLoading: unblocking } = useUnblockUser();

  if (isLoading) return null;
  if (blockedUsers.length === 0) return null;

  return (
    <section className="bg-white dark:bg-zinc-900 rounded-2xl border border-gray-200 dark:border-zinc-800 shadow-sm p-6 mb-6">
      <h2 className="font-bold text-lg mb-4 flex items-center gap-2">
        <Ban className="h-5 w-5 text-red-500" />
        Blocked Users
      </h2>
      <p className="text-sm text-gray-500 dark:text-zinc-500 mb-4">
        Blocked users cannot send you messages. You can unblock them at any time.
      </p>
      <ul className="space-y-3">
        {blockedUsers.map((b: any) => (
          <li key={b.id} className="flex items-center justify-between gap-4 py-2 border-b border-gray-100 dark:border-zinc-800 last:border-0">
            <span className="text-sm font-medium text-gray-800 dark:text-zinc-200">@{b.blocked_username}</span>
            <button
              onClick={() => unblock(b.blocked_user_id)}
              disabled={unblocking}
              className="text-xs text-brand-600 dark:text-brand-400 hover:underline disabled:opacity-50"
            >
              Unblock
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

function ChangePasswordButton() {
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleClick = async () => {
    setLoading(true);
    try {
      await apiClient.post('/auth/request-password-change');
      setSent(true);
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
        <CheckCircle className="h-4 w-4" />
        Check your email — a confirmation link has been sent.
      </div>
    );
  }

  return (
    <button
      type="button"
      disabled={loading}
      className="btn-outline py-2 px-5 text-sm disabled:opacity-50"
      onClick={handleClick}
    >
      {loading ? 'Sending…' : 'Change Password'}
    </button>
  );
}

export default function SettingsPage() {
  const { isDark, toggleTheme } = useThemeStore();
  const { data: user } = useCurrentUser();
  const { mutate: uploadAvatar, isLoading: uploading } = useUploadAvatar();
  const { mutate: deleteAvatar, isLoading: deleting } = useDeleteAvatar();
  const { mutate: updateProfile, isLoading: saving } = useUpdateProfile();
  const { data: emailPrefs, isLoading: prefsLoading } = useEmailPreferences();
  const { mutate: updateEmailPrefs, isLoading: prefsSaving } = useUpdateEmailPreferences();
  const { mutate: sendVerification, isLoading: sendingVerification } = useSendVerificationEmail();
  const { mutate: requestDelete, isLoading: requestingDelete } = useRequestDeleteAccount();
  const { data: connectStatus, isLoading: connectLoading } = useConnectStatus();
  const { mutate: onboardConnect, isLoading: onboarding } = useOnboardConnect();
  const logout = useAuthStore((state) => state.logout);
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName]   = useState('');
  const [bio, setBio]             = useState('');

  // Populate fields once user loads
  useEffect(() => {
    if (!user) return;
    setFirstName(user.first_name ?? '');
    setLastName(user.last_name ?? '');
    setBio(user.bio ?? '');
  }, [user]);

  const [cropSrc, setCropSrc] = useState<string | null>(null);
  const [cropFileName, setCropFileName] = useState('avatar.jpg');

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setCropFileName(file.name);
      setCropSrc(URL.createObjectURL(file));
    }
    e.target.value = '';
  };
  const handleCropConfirm = useCallback((file: File) => {
    uploadAvatar(file);
    if (cropSrc) URL.revokeObjectURL(cropSrc);
    setCropSrc(null);
  }, [uploadAvatar, cropSrc]);
  const handleCropCancel = useCallback(() => {
    if (cropSrc) URL.revokeObjectURL(cropSrc);
    setCropSrc(null);
  }, [cropSrc]);

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    updateProfile({ first_name: firstName, last_name: lastName, bio });
  };

  return (
    <div className="bg-white dark:bg-zinc-900 min-h-screen">
      {cropSrc && (
        <AvatarCropModal
          imageSrc={cropSrc}
          fileName={cropFileName}
          onConfirm={handleCropConfirm}
          onCancel={handleCropCancel}
        />
      )}
      {/* Page header */}
      <div className="bg-gradient-to-r from-brand-600 to-accent-600 text-white py-10">
        <div className="container-custom">
          <h1 className="text-3xl font-display font-bold">Account Settings</h1>
          <p className="text-brand-100 mt-1">Manage your profile and preferences</p>
        </div>
      </div>

      <div className="container-custom py-8 max-w-2xl">

        {/* ── Appearance ──────────────────────────────────────────── */}
        <section className="bg-white dark:bg-zinc-900 rounded-2xl border border-gray-200 dark:border-zinc-800 shadow-sm p-6 mb-6">
          <h2 className="font-bold text-lg mb-5 flex items-center gap-2">
            <Palette className="h-5 w-5 text-brand-500" />
            Appearance
          </h2>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {isDark
                ? <Moon className="h-5 w-5 text-brand-400" />
                : <Sun className="h-5 w-5 text-amber-500" />
              }
              <div>
                <p className="font-medium text-sm text-gray-900 dark:text-zinc-100">
                  {isDark ? 'Dark mode' : 'Light mode'}
                </p>
                <p className="text-xs text-gray-500 dark:text-zinc-500">
                  {isDark ? 'Charcoal theme is active' : 'Light theme is active'}
                </p>
              </div>
            </div>
            <button
              onClick={toggleTheme}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 dark:focus:ring-offset-zinc-900 ${
                isDark ? 'bg-brand-600' : 'bg-gray-200'
              }`}
              role="switch"
              aria-checked={isDark}
              aria-label="Toggle dark mode"
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${
                  isDark ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </section>

        {/* ── Profile Picture ─────────────────────────────────────── */}
        <section className="bg-white dark:bg-zinc-900 rounded-2xl border border-gray-200 dark:border-zinc-800 shadow-sm p-6 mb-6">
          <h2 className="font-bold text-lg mb-5 flex items-center gap-2">
            <User className="h-5 w-5 text-brand-500" />
            Profile Picture
          </h2>

          <div className="flex items-center gap-6">
            {/* Avatar preview */}
            <div className="relative group">
              <UserAvatar
                username={user?.username ?? '?'}
                avatarUrl={user?.avatar_url}
                size="xl"
              />
              {/* hover overlay */}
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="absolute inset-0 rounded-full bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer"
                aria-label="Change profile picture"
              >
                <Camera className="h-6 w-6 text-white" />
              </button>
            </div>

            {/* Actions */}
            <div className="flex flex-col gap-3">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="btn-primary py-2 px-5 text-sm flex items-center gap-2 disabled:opacity-50"
              >
                <Camera className="h-4 w-4" />
                {uploading ? 'Uploading…' : user?.avatar_url ? 'Replace Photo' : 'Upload Photo'}
              </button>

              {user?.avatar_url && (
                <button
                  type="button"
                  onClick={() => {
                    if (window.confirm('Remove your profile picture?')) deleteAvatar();
                  }}
                  disabled={deleting}
                  className="flex items-center gap-2 px-5 py-2 rounded-lg border border-red-200 text-red-600 text-sm font-medium hover:bg-red-50 dark:hover:bg-red-900/20 dark:bg-red-900/20 transition-colors disabled:opacity-50"
                >
                  <Trash2 className="h-4 w-4" />
                  {deleting ? 'Removing…' : 'Remove Photo'}
                </button>
              )}

              <p className="text-xs text-gray-500 dark:text-zinc-500">JPG, PNG or WebP · max 5 MB</p>
            </div>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={handleFileChange}
          />
        </section>

        {/* ── Email Notifications ───────────────────────────────────── */}
        <section className="bg-white dark:bg-zinc-900 rounded-2xl border border-gray-200 dark:border-zinc-800 shadow-sm p-6 mb-6">
          <h2 className="font-bold text-lg mb-1 flex items-center gap-2">
            <Mail className="h-5 w-5 text-brand-500" />
            Email Notifications
          </h2>
          <p className="text-sm text-gray-500 dark:text-zinc-500 mb-5">
            Choose which events send you an email. Emails are only sent when you opt in — all notifications still appear in-app regardless.
          </p>

          {prefsLoading ? (
            <div className="space-y-3">
              {[...Array(7)].map((_, i) => <div key={i} className="skeleton h-12 rounded-xl" />)}
            </div>
          ) : (
            <div className="divide-y divide-gray-200 dark:divide-zinc-800">
              {EMAIL_PREF_OPTIONS.map(({ key, label, description }) => {
                const enabled = emailPrefs?.[key] ?? false;
                return (
                  <div key={key} className="flex items-center justify-between gap-4 py-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-zinc-100">{label}</p>
                      <p className="text-xs text-gray-500 dark:text-zinc-500 mt-0.5">{description}</p>
                    </div>
                    <button
                      type="button"
                      disabled={prefsSaving}
                      onClick={() => updateEmailPrefs({ ...emailPrefs, [key]: !enabled })}
                      className={`relative flex-shrink-0 inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 disabled:opacity-50 ${
                        enabled ? 'bg-brand-600' : 'bg-gray-200 dark:bg-zinc-700'
                      }`}
                      role="switch"
                      aria-checked={enabled}
                      aria-label={label}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white dark:bg-zinc-900 shadow transition-transform ${enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          <div className="mt-4 flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-100 rounded-xl">
            <Bell className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-700 dark:text-amber-300">
              To receive emails, your account administrator must configure the SMTP settings on the server.
              Contact support if emails aren't arriving.
            </p>
          </div>
        </section>

        {/* ── Email Verification ───────────────────────────────────── */}
        <section className="bg-white dark:bg-zinc-900 rounded-2xl border border-gray-200 dark:border-zinc-800 shadow-sm p-6 mb-6">
          <h2 className="font-bold text-lg mb-1 flex items-center gap-2">
            <BadgeCheck className="h-5 w-5 text-brand-500" />
            Email Verification
          </h2>
          <p className="text-sm text-gray-500 dark:text-zinc-500 mb-5">
            Verify your email to get a checkmark badge on your profile visible to other users.
          </p>

          {user?.email_verified ? (
            <div className="flex items-center gap-3 p-3 bg-green-50 dark:bg-green-900/20 border border-green-100 rounded-xl">
              <ShieldCheck className="h-5 w-5 text-green-500 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-green-800 dark:text-green-200">Email verified</p>
                <p className="text-xs text-green-600">{user.email}</p>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between gap-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-100 rounded-xl">
              <div>
                <p className="text-sm font-medium text-amber-800 dark:text-amber-200">Email not verified</p>
                <p className="text-xs text-amber-600">{user?.email}</p>
              </div>
              <button
                type="button"
                onClick={() => sendVerification()}
                disabled={sendingVerification}
                className="btn-primary py-1.5 px-4 text-sm disabled:opacity-50 flex-shrink-0"
              >
                {sendingVerification ? 'Sending…' : 'Send verification email'}
              </button>
            </div>
          )}
        </section>

        {/* ── Password ─────────────────────────────────────────────── */}
        <section className="bg-white dark:bg-zinc-900 rounded-2xl border border-gray-200 dark:border-zinc-800 shadow-sm p-6 mb-6">
          <h2 className="font-bold text-lg mb-1 flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-brand-500" />
            Password
          </h2>
          <p className="text-sm text-gray-500 dark:text-zinc-500 mb-5">
            We'll send a confirmation email before allowing any password change.
          </p>
          <ChangePasswordButton />
        </section>

        {/* ── Seller Payouts (Stripe Connect) ──────────────────────── */}
        <section className="bg-white dark:bg-zinc-900 rounded-2xl border border-gray-200 dark:border-zinc-800 shadow-sm p-6 mb-6">
          <h2 className="font-bold text-lg mb-1 flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-brand-500" />
            Seller Payouts
          </h2>
          <p className="text-sm text-gray-500 dark:text-zinc-500 mb-5">
            Connect a bank account to receive payouts when your cards sell. Required to accept payments directly.
            If you don't connect an account, sales can still happen but payouts will be handled manually.
          </p>

          {connectLoading ? (
            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-zinc-500">
              <Loader className="h-4 w-4 animate-spin" /> Checking status…
            </div>
          ) : connectStatus?.connected && connectStatus?.onboarding_complete ? (
            <div className="space-y-3">
              <div className="flex items-center gap-3 p-3 bg-green-50 dark:bg-green-900/20 border border-green-100 rounded-xl">
                <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-green-800 dark:text-green-200">Payouts enabled</p>
                  <p className="text-xs text-green-600">
                    {connectStatus.charges_enabled ? 'Accepting payments' : 'Pending Stripe review'} ·{' '}
                    {connectStatus.payouts_enabled ? 'Payouts active' : 'Payouts pending'}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => onboardConnect(undefined, { onSuccess: ({ url }) => { window.location.href = url; } })}
                disabled={onboarding}
                className="flex items-center gap-2 text-sm text-brand-600 dark:text-brand-400 hover:underline font-medium"
              >
                <ExternalLink className="h-4 w-4" />
                Manage payout settings in Stripe
              </button>
            </div>
          ) : connectStatus?.connected && !connectStatus?.onboarding_complete ? (
            <div className="space-y-3">
              <div className="flex items-center gap-3 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-100 rounded-xl">
                <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0" />
                <p className="text-sm text-amber-800 dark:text-amber-200">Stripe account created but onboarding is incomplete.</p>
              </div>
              <button
                type="button"
                onClick={() => onboardConnect(undefined, { onSuccess: ({ url }) => { window.location.href = url; } })}
                disabled={onboarding}
                className="btn-primary py-2 px-5 text-sm flex items-center gap-2 disabled:opacity-50"
              >
                {onboarding ? <Loader className="h-4 w-4 animate-spin" /> : <ExternalLink className="h-4 w-4" />}
                Continue Stripe onboarding
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => onboardConnect(undefined, { onSuccess: ({ url }) => { window.location.href = url; } })}
              disabled={onboarding}
              className="btn-primary py-2 px-5 text-sm flex items-center gap-2 disabled:opacity-50"
            >
              {onboarding ? <Loader className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
              {onboarding ? 'Redirecting to Stripe…' : 'Connect a bank account'}
            </button>
          )}
        </section>

        {/* ── Blocked Users ─────────────────────────────────────────── */}
        <BlockedUsersSection />

        {/* ── Delete Account ────────────────────────────────────────── */}
        <section className="bg-white dark:bg-zinc-900 rounded-2xl border border-red-100 shadow-sm p-6 mb-6">
          <h2 className="font-bold text-lg mb-1 flex items-center gap-2 text-red-600">
            <AlertTriangle className="h-5 w-5" />
            Delete Account
          </h2>
          <p className="text-sm text-gray-500 dark:text-zinc-500 mb-5">
            Permanently delete your account and all associated data. This cannot be undone.
            A confirmation link will be sent to your email.
          </p>

          {!deleteConfirm ? (
            <button
              type="button"
              onClick={() => setDeleteConfirm(true)}
              className="px-5 py-2 rounded-lg border border-red-200 text-red-600 text-sm font-medium hover:bg-red-50 dark:hover:bg-red-900/20 dark:bg-red-900/20 transition-colors"
            >
              Delete my account
            </button>
          ) : (
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => { requestDelete(); setDeleteConfirm(false); }}
                disabled={requestingDelete}
                className="px-5 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {requestingDelete ? 'Sending…' : 'Yes, send confirmation email'}
              </button>
              <button
                type="button"
                onClick={() => setDeleteConfirm(false)}
                className="px-5 py-2 rounded-lg border border-gray-200 dark:border-zinc-800 text-gray-600 dark:text-zinc-400 text-sm font-medium hover:bg-white dark:hover:bg-zinc-800 dark:bg-zinc-900 transition-colors"
              >
                Cancel
              </button>
            </div>
          )}
        </section>

        {/* ── Profile Info ─────────────────────────────────────────── */}
        <section className="bg-white dark:bg-zinc-900 rounded-2xl border border-gray-200 dark:border-zinc-800 shadow-sm p-6">
          <h2 className="font-bold text-lg mb-5">Profile Info</h2>

          <form onSubmit={handleSave} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-zinc-300 mb-1">First name</label>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="input w-full"
                  placeholder="First name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-zinc-300 mb-1">Last name</label>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="input w-full"
                  placeholder="Last name"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-zinc-300 mb-1">Username</label>
              <input
                type="text"
                value={user?.username ?? ''}
                disabled
                className="input w-full bg-white dark:bg-zinc-900 text-gray-500 dark:text-zinc-500 cursor-not-allowed"
              />
              <p className="text-xs text-gray-500 dark:text-zinc-500 mt-1">Username cannot be changed</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-zinc-300 mb-1">Bio</label>
              <textarea
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                rows={3}
                maxLength={300}
                className="input w-full resize-none"
                placeholder="Tell other collectors about yourself…"
              />
              <p className="text-xs text-gray-500 dark:text-zinc-500 mt-1 text-right">{bio.length}/300</p>
            </div>

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={saving}
                className="btn-primary py-2 px-6 text-sm flex items-center gap-2 disabled:opacity-50"
              >
                <Save className="h-4 w-4" />
                {saving ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </form>
        </section>

      </div>
    </div>
  );
}
